'use strict';

// utilities
import { EventEmitter } from 'events';
import _ from 'lodash';

// components
import AppDispatcher from '../core/AppDispatcher.jsx';
import Constants from '../constants/PensionTypeConstants.js';
import ApplicationConstants from '../constants/ApplicationConstants.js';
import SelectCustomerConstants from '../constants/SelectCustomerConstants.js';

import Mapper from '../utils/Mapper.js';
import Products from '../utils/Products.js';
var DateTime = require('../utils/DateTime.js');
import Validators from '../utils/Validators.js';
import ContentService from '../services/ContentService.js';

// local variables
const PensionTypeActionType = Constants.ActionTypes;
const AppActionType = ApplicationConstants.ActionTypes;
const CopyText = ContentService.getContent();
const CHANGE_EVENT = 'change';
const STRING_EMPTY = '';
const COMPONENT_ID = 'ptPensionTypeSelection';
const PENSION_TYPE_NAME = 'Pension Type';
const FIELD_CURRENT_AGE = 'currentAge';
const FIELD_DATE_OF_BIRTH = 'dateOfBirth';
const FIELD_BENEFIT_ITEM = 'selectedBenefitItem';

let _model = {},
    _client,
    _selection = _.cloneDeep(Constants.PensionTypeSelection),
    _formErrors = [],
    _summaryErrors = [],
    _productId = STRING_EMPTY,
    _isValid = false,
    _pensionType = '',
    _crossSectionDependencyMsg = [];

let _customAttributeName = {
    dateOfBirth: 'Date of birth',
    selectedBenefitItem: 'Pension type',
    currentAge: 'Date of birth'
};
let _rules = {
    dateOfBirth: 'required|pensionDobMinimumAge',
    selectedBenefitItem: 'required',
    currentAge: 'required'
};

function getRules(fieldName) {
    let allRules = _rules,
        rulesForField = {};

    if (!fieldName) {
        return allRules;
    }

    if (!allRules[fieldName]) {
        return null;
    }

    rulesForField[fieldName] = allRules[fieldName];

    return rulesForField;
}

function setClient(client) {
    _client = _.cloneDeep(client);
    _crossSectionDependencyMsg = [];
}

function prePopulateWithExistingClientIfAny() {
    if (!_client) {
        return;
    }

    // set date of birth
    if (_productId === ApplicationConstants.ProductIds.PENSION
        && _client.customerType === ApplicationConstants.ClientTypes.INDIVIDUAL) {
        validateDateOfBirthField(_client.dateOfBirth, COMPONENT_ID);
        initialize(_client.dateOfBirth, Constants.PensionTypeSelection);
    }
}

function initStore(productId) {
    _productId = productId;
    _model = _.cloneDeep(Constants.PensionTypeData);
    _selection = _.cloneDeep(Constants.PensionTypeSelection);
    _summaryErrors = [];
    _crossSectionDependencyMsg = [];
}

// Setter functions to set data, removed from Store Class
function initialize(dob, pensionTypeSelection) {
    if (_isValid) {
        setAgeFromDateOfBirth(dob);
        setPensionTypeSelections(pensionTypeSelection);
    }
}

function setAgeFromDateOfBirth(dob) {
    _model.currentAge = DateTime.getAge(new Date(), dob);
}

function setPensionTypeSelections(pensionTypeSelection) {
    // set pension type selections
    _selection = pensionTypeSelection
        .filter(function (element) {
            return element.minAgeLimit <= _model.currentAge && _model.currentAge <= element.maxAgeLimit;
        });
}

function selectPensionType(fieldId, value) {
    if (_isValid) {
        setGeneralDisclaimer(true);
    } else {
        if (!DateTime.isBirthDateValid(_model.dateOfBirth, _productId)) {
            validateFields(FIELD_DATE_OF_BIRTH, _model[FIELD_DATE_OF_BIRTH], COMPONENT_ID);
            resetPensionTypeSection();
        }
    }
    autoSelectBenefitItem(value);
    select(value);
}

function autoSelectBenefitItem(selectedPensionType) {
    switch (selectedPensionType) {
        case Constants.PensionTypeSelectionNames.PRE_RETIREMENT:
            _model.selectedBenefitItem = Constants.BenefitSelectionNames.PRE_RETIREMENT;
            break;

        case Constants.PensionTypeSelectionNames.RETIREMENT:
            _model.selectedBenefitItem = STRING_EMPTY;
            if (_model.currentAge < Constants.PreRetirementAge) {
                _model.selectedBenefitItem = Constants.BenefitSelectionNames.RETIREMENT_PRESERVE_AGE;
            }
            else if (_model.currentAge >= Constants.RetirementAge) {
                _model.selectedBenefitItem = Constants.BenefitSelectionNames.RETIREMENT;
            }
            break;

        default:
            if (_model.selectedPensionType !== selectedPensionType) _model.selectedBenefitItem = STRING_EMPTY;
            break;
    }
}

function select(selectedPensionType) {
    var pensionTypeSelected = _selection
        .filter(function (pensionType) {
            return pensionType.id === selectedPensionType
        });

    if (pensionTypeSelected && (pensionTypeSelected.length > 0)) {
        _model.selectedPensionType = pensionTypeSelected[0].id;
    }
}

function setGeneralDisclaimer(value) {
    _model.showGeneralDisclaimer = value;
}

function resetPensionTypeSection() {
    _model.selectedPensionType = '';
    _model.selectedBenefitItem = '';
    _model.currentAge = '';
    setGeneralDisclaimer(false);
}

function selectBenefit(selectedBenefit) {
    if (_isValid) {
        _model.selectedBenefitItem = selectedBenefit;
    } else {
        if (!DateTime.isBirthDateValid(_model.dateOfBirth, _productId)) {
            validateFields(FIELD_DATE_OF_BIRTH, _model[FIELD_DATE_OF_BIRTH], COMPONENT_ID);
            resetPensionTypeSection();
            _model.selectedPensionType = _selection[_selection.length-1].id;
            _model.selectedBenefitItem = '';
        }
    }
}

// clear _formErrors to start on a clean slate
function clearFormErrors() {
    _formErrors = [];
    _summaryErrors = [];
}

// for validating date-of-birth field
function validateDateOfBirthField(dob, componentId) {

    if (DateTime.isBirthDateValid(dob, _productId)) {
        let field = FIELD_CURRENT_AGE;

        _model = _.clone(Constants.PensionTypeData);
        _model.dateOfBirth = dob;

        if (Validators.requiredValidation(dob)) {
            setAgeFromDateOfBirth(dob);
            field = FIELD_DATE_OF_BIRTH;
        }
        validateFields(field, _model[field], componentId);
        _isValid = !(_formErrors && _formErrors.length > 0); // invalid if it contains an item
    } else {
        _isValid = false;
        if (dob) {
            _model = _.clone(Constants.PensionTypeData);    
        } else {
            _model.dateOfBirth = '';
        }
    }
    _.remove(_formErrors, function (o) { return o.message === CopyText.pensionType.missingDOB; });
}

// for validating date-of-birth/current age value
function validateAgeValue(componentId) {
    let field = FIELD_CURRENT_AGE; // field for checking if date of birth is required

    // select appropriate field for error
    if (Validators.requiredValidation(_model.dateOfBirth) && (Mapper.defaultsTo(_model.currentAge, 0) < Constants.MinimumValidAge)) {
        field = FIELD_DATE_OF_BIRTH; // this field for checking of minimum valid age
    }
    validateFields(field, _model[field], componentId);
    _isValid = !(_formErrors && _formErrors.length > 0); // invalid if it contains an item
}

function validateFields(fieldId, value, componentId) {
    _model[fieldId] = Mapper.toTrim(value);
    Validators.validateField(_model, getRules(fieldId), _customAttributeName, _formErrors, fieldId, componentId);
}

function clearAndValidateAgeValue(componentId) {
    if (DateTime.isBirthDateValid(_model.dateOfBirth, _productId)) {
        clearFormErrors();
        validateAgeValue(componentId);
    } else {
        _isValid = false;
    }
}

function validateAllRequiredFields() {
    var field = _isValid? FIELD_BENEFIT_ITEM : FIELD_DATE_OF_BIRTH;
    if (_productId === ApplicationConstants.ProductIds.PENSION) {

        if (_isValid) {
            updateCustomAttributeName();
        } else {
            if (!_model.dateOfBirth) {
                resetPensionTypeSection();
                _model.selectedPensionType = _selection[0].id;
            }
        }
        
        // using COMPONENT_ID instead of Id here to force error to display on the section
        _formErrors = Validators.validateAll(_model, getRules(field), _customAttributeName, COMPONENT_ID);
        _summaryErrors = Validators.validateAll(_model, getRules(field), _customAttributeName, COMPONENT_ID, PENSION_TYPE_NAME);
    }
}

// update custom attribute name if error falls under RETIREMENT and MORE
function updateCustomAttributeName() {
    switch (_model.selectedPensionType) {
        case Constants.PensionTypeSelectionNames.RETIREMENT:
            _customAttributeName.selectedBenefitItem = 'Retirement benefit';
            break;

        case Constants.PensionTypeSelectionNames.MORE:
            _customAttributeName.selectedBenefitItem = 'Benefit type';
            break;
    }
}

function retrievePensionType(form) {
    if (form.pensionType) {
        _model = _.cloneDeep(form.pensionType);
    }

    if (form.yourDetails) {
        if (form.yourDetails.dateOfBirth) {
            _model.dateOfBirth = form.yourDetails.dateOfBirth;
            
            setAgeFromDateOfBirth(_model.dateOfBirth);
            setPensionTypeSelections(Constants.PensionTypeSelection);

            if (Products.isPension(_productId)) {
                if (DateTime.isBirthDateValid(_model.dateOfBirth, _productId) && Validators.requiredValidation(_model.dateOfBirth)) {
                    validateFields(FIELD_DATE_OF_BIRTH, _model[FIELD_DATE_OF_BIRTH], COMPONENT_ID);
                    _isValid = !(_formErrors && _formErrors.length > 0);
                } else {
                    _isValid = false;
                }
            }
        } else {
            _model.dateOfBirth = '';
        }
    }

    clearFormErrors();
}

function modifyFormErrors() {
    var dobFormErrorIndex = _.findIndex(_formErrors, function (n) { return n.message === CopyText.pensionType.missingDOB; }),
        summaryFormErrorIndex = _.findIndex(_summaryErrors, function (n) { return n.message === CopyText.pensionType.missingDOBForSummary; });

    if (dobFormErrorIndex > -1) {
        _formErrors[dobFormErrorIndex].message =  CopyText.pensionType.missingDOBInline;
    }
    if (summaryFormErrorIndex > -1) {
        _summaryErrors[summaryFormErrorIndex].message =  CopyText.pensionType.missingDOBSummary;    
    }
}

function pensionTypeChanged(pensionType, investmentListAvailable) {
    // when switch from/to Death benefit, funding types have to be refreshed
    var content = ContentService.getContent();
    if (pensionType !== _pensionType &&
        (pensionType === Constants.PensionTypeBenefits.ENTRY_DEATH ||
        _pensionType === Constants.PensionTypeBenefits.ENTRY_DEATH ||
        pensionType === Constants.PensionTypeSelectionNames.PRE_RETIREMENT ||
        _pensionType === Constants.PensionTypeSelectionNames.PRE_RETIREMENT )) {

        updateCrossSectionDependency(content.crossSectionHeaders.fundingPension);
    } 
    else if (pensionType !== _pensionType && !investmentListAvailable &&
             (pensionType === Constants.PensionTypeSelectionNames.PRE_RETIREMENT ||
              pensionType === Constants.PensionTypeSelectionNames.RETIREMENT ||
              pensionType === Constants.PensionTypeSelectionNames.MORE)) {

        updateCrossSectionDependency(content.crossSectionHeaders.investmentAllocation);
    }
    _pensionType = pensionType;
}


function sortCrossSectionOrder() {
    _crossSectionDependencyMsg = _.sortBy(_crossSectionDependencyMsg, 'order');
}

function updateCrossSectionDependency(dependencySection) {
    _crossSectionDependencyMsg = [];
    if (Products.isPension(_productId)) {
        _crossSectionDependencyMsg.push({content: dependencySection, order: 1});
    }
}

// store class
class PensionTypeStoreClass extends EventEmitter {
    constructor() {
        super();
    }

    getPensionTypeModel() {
        return _.clone(_model);
    }

    getSelection() {
        return (_selection && _selection.length === 0) ? Constants.PensionTypeSelection : _selection;
    }

    getFormError() {
        modifyFormErrors();
        return _formErrors;
    }

    pensionTypeErrorMessages() {
        modifyFormErrors();
        return _summaryErrors;
    }

    addChangeListener(callback) {
        this.on(CHANGE_EVENT, callback);
    }

    removeChangeListener(callback) {
        this.removeListener(CHANGE_EVENT, callback);
    }

    emitChange() {
        this.emit(CHANGE_EVENT);
    }

    getCrossSectionDependencyMsg() {
        sortCrossSectionOrder();
        return _crossSectionDependencyMsg;
    }
}

const PensionTypeStore = new PensionTypeStoreClass();


PensionTypeStore.dispatchToken = AppDispatcher.register((action) => {
    switch (action.actionType) {
        case AppActionType.SEND_YOUR_DETAIL_DOB:
            // trigger validation of fields first, before running actual action
            validateDateOfBirthField(action.dob, COMPONENT_ID);
            initialize(action.dob, Constants.PensionTypeSelection);
            break;

        case PensionTypeActionType.SELECT_PENSION_TYPE:
            // clear and trigger validation of fields first, before running actual action
            clearAndValidateAgeValue(action.componentId);
            selectPensionType(action.fieldId, action.value);
            pensionTypeChanged(action.value, action.investmentListAvailable);
            break;

        case PensionTypeActionType.SELECT_BENEFIT:
            // clear and trigger validation of fields first, before running actual action to prevent incorrect behavior
            clearAndValidateAgeValue(action.componentId);
            selectBenefit(action.value);
            pensionTypeChanged(action.value);
            break;

        case AppActionType.VALIDATE_APPLICATION:
            validateAllRequiredFields();
            break;

        case ApplicationConstants.ActionTypes.NEW_APPLICATION_INITIALISED:
            initStore(action.productId);
            prePopulateWithExistingClientIfAny();
            break;

        case ApplicationConstants.ActionTypes.APPLICATION_RETRIEVE_SUCCESS:
            initStore(action.applicationData.productId);
            retrievePensionType(action.applicationData.form);
            break;

        case SelectCustomerConstants.ActionTypes.CLIENT_RETRIEVE_COMPLETED:
            setClient(action.client);
            break;

        default:
            return true;
    }

    PensionTypeStore.emitChange();
    return true;
});

export default PensionTypeStore;


// WEBPACK FOOTER //
// C:/GH/1106/advice.noa.origination/~/eslint-loader!./stores/PensionTypeStore.js